import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { showErrorToast } from "../../components/CustomComponents/toast/toast";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { setFilterField } from "../../app/itemGridPreferencesSlice";
import DateRangePicker from "../../components/CustomComponents/DateRangePicker/DateRangePicker";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import { formatCompanyDateTimeForApi } from "../../utils/companyDateTime";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import "./../order/ManageOrderFilters.css";

const toValidDate = (value) => {
  if (value == null) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return date instanceof Date && !isNaN(date.getTime()) ? date : null;
};

const packageTypeLabel = (type) => {
  const t = String(type || "").toUpperCase();
  if (t === "COMBO_DEAL") return "Deal";
  if (t === "COMBO") return "Combo";
  return type || "—";
};

const PAGE_KEY = "dealPerformanceReport";
const DEFAULT_LOCATION = { value: "all", label: "All Locations" };
const DEFAULT_PACKAGE_TYPE = { value: "all", label: "Deals & Combos" };
const DEFAULT_VIEW_MODE = { value: "packages", label: "By Package" };
const PACKAGE_TYPE_OPTIONS = [
  { value: "all", label: "Deals & Combos" },
  { value: "COMBO_DEAL", label: "Deals only" },
  { value: "COMBO", label: "Combos only" },
];
const VIEW_MODE_OPTIONS = [
  { value: "packages", label: "By Package" },
  { value: "details", label: "Order Lines" },
];

const DealPerformanceReport = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const user = useSelector((state) => state.auth.user);
  const { profileData } = useSelector((state) => state.auth);
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const { getCompCurrency, getCompTimeZone, getCompUTCTimeZone } = useProfileAndSettings();
  const compCurrency = getCompCurrency();
  const compTimeZone = getCompTimeZone();
  const compUTCTimeZone = getCompUTCTimeZone();
  const companyTzOptions = useMemo(
    () => ({ compTimeZone, compUTCTimeZone }),
    [compTimeZone, compUTCTimeZone]
  );

  const defaultDateRange = useMemo(
    () => [moment().startOf("week").toDate(), moment().endOf("week").toDate()],
    []
  );

  const preferences = useSelector((state) => state.itemGridPreferences[PAGE_KEY] || {});
  const location = preferences.location || DEFAULT_LOCATION;
  const packageType = preferences.packageType || DEFAULT_PACKAGE_TYPE;
  const viewMode = preferences.viewMode || DEFAULT_VIEW_MODE;
  // Coerce cleared/null dates back to default "This Week"
  const dateRange = useMemo(() => {
    const stored = preferences.dateRange;
    if (!Array.isArray(stored)) return defaultDateRange;
    const start = toValidDate(stored[0]);
    const end = toValidDate(stored[1]);
    if (!start && !end) return defaultDateRange;
    return [start || defaultDateRange[0], end || defaultDateRange[1]];
  }, [preferences.dateRange, defaultDateRange]);
  const rawStartDate = dateRange?.[0] ?? null;
  const rawEndDate = dateRange?.[1] ?? null;
  const locationValue = location?.value ?? "all";
  const packageTypeValue = packageType?.value ?? "all";
  const viewModeValue = viewMode?.value || "packages";
  const startKey = rawStartDate ? moment(rawStartDate).valueOf() : 0;
  const endKey = rawEndDate ? moment(rawEndDate).valueOf() : 0;

  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();

  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (code) => currencySymbols[code] || code || "";

  const parentIdForLocation =
    CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";

  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [packages, setPackages] = useState([]);
  const [details, setDetails] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "20", label: "20" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const searchTimeoutRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const summaryAbortRef = useRef(null);
  const lastUserIdentityRef = useRef(null);
  const skipPageResetRef = useRef(true);

  const pageSizeValue = Number(pageSize?.value ?? pageSize ?? 20) || 20;

  const locationOptions =
    locations.length > 0
      ? [DEFAULT_LOCATION, ...locations]
      : parentIdForLocation != null && !isNaN(parentIdForLocation)
        ? [DEFAULT_LOCATION, { value: parentIdForLocation, label: companyName }]
        : [DEFAULT_LOCATION];

  const buildFilterParams = useCallback(() => {
    const startDate = toValidDate(rawStartDate) || moment().startOf("week").toDate();
    const endDate = toValidDate(rawEndDate) || moment().endOf("week").toDate();
    const formattedStartDate =
      formatCompanyDateTimeForApi(startDate, companyTzOptions) ||
      moment(startDate).format("YYYY-MM-DD HH:mm:ss");
    const formattedEndDate =
      formatCompanyDateTimeForApi(endDate, companyTzOptions) ||
      moment(endDate).format("YYYY-MM-DD HH:mm:ss");

    const params = {
      CompID,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
    if (locationValue && locationValue !== "all") {
      params.locationFilter = locationValue;
    }
    if (packageTypeValue && packageTypeValue !== "all") {
      params.packageTypeFilter = packageTypeValue;
    }
    if (committedSearch) {
      params.searchTerm = committedSearch;
    }
    return params;
  }, [
    CompID,
    rawStartDate,
    rawEndDate,
    companyTzOptions,
    locationValue,
    packageTypeValue,
    committedSearch,
  ]);

  const handleFilterChange = (filterName, selectedOption) => {
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: filterName, value: selectedOption }));
  };

  const clearAllFilters = () => {
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "location", value: DEFAULT_LOCATION }));
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "packageType", value: DEFAULT_PACKAGE_TYPE }));
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "viewMode", value: DEFAULT_VIEW_MODE }));
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: defaultDateRange }));
    setSearch("");
    setCommittedSearch("");
    setSummary(null);
    setPageNumber(1);
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = Array.isArray(update) ? update : [update, null];
    // Picker clear → restore default "This Week" (same as Clear filters)
    if (
      (!newStartDate || !moment(newStartDate).isValid()) &&
      (!newEndDate || !moment(newEndDate).isValid())
    ) {
      dispatch(setFilterField({ pageKey: PAGE_KEY, field: "dateRange", value: defaultDateRange }));
      return;
    }
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      let effectiveStartDate = newStartDate;
      let effectiveEndDate = newEndDate;
      if (newStartDate && newEndDate && newStartDate > newEndDate) {
        [effectiveStartDate, effectiveEndDate] = [newEndDate, newStartDate];
      } else if (newStartDate && !newEndDate) {
        effectiveEndDate = new Date(newStartDate.getTime());
        effectiveEndDate.setHours(23, 59, 59, 999);
      }
      dispatch(
        setFilterField({
          pageKey: PAGE_KEY,
          field: "dateRange",
          value: [effectiveStartDate, effectiveEndDate],
        })
      );
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setCommittedSearch(search.trim()), 400);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search]);

  // Clear on-demand summary when report filters change
  useEffect(() => {
    setSummary(null);
  }, [startKey, endKey, locationValue, packageTypeValue, viewModeValue, committedSearch]);

  // Reset to page 1 when filters or page size change
  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setPageNumber(1);
  }, [startKey, endKey, locationValue, packageTypeValue, viewModeValue, committedSearch, pageSizeValue]);

  const fetchLocations = useCallback(async () => {
    if (!CompID || !isMain) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
      const base = [{ value: parentId, label: compName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          return pidNum === parentId && idNum !== parentId;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  const fetchReport = useCallback(async () => {
    if (!CompID) return;

    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        includeDetails: viewModeValue === "details",
        pageNumber,
        pageSize: pageSizeValue,
      };

      const response = await axiosInstance.get(APIs.GET_DEAL_PERFORMANCE_REPORT, {
        params,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const ok = response.data?.status === true || response.data?.Status === true;
      if (ok) {
        const payload = response.data.data || response.data.Data || {};
        setPackages(payload.packages || payload.Packages || []);
        setDetails(payload.details || payload.Details || []);
        const total = Number(payload.totalCount ?? payload.TotalCount ?? 0);
        const pages = Number(payload.totalPages ?? payload.TotalPages ?? 0);
        setTotalRecords(total);
        setTotalPages(pages || (pageSizeValue > 0 ? Math.ceil(total / pageSizeValue) : 0));
      } else {
        setPackages([]);
        setDetails([]);
        setTotalRecords(0);
        setTotalPages(0);
        showErrorToast(
          response.data?.message || response.data?.Message || "Unable to fetch deal performance."
        );
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      console.error(err);
      setPackages([]);
      setDetails([]);
      setTotalRecords(0);
      setTotalPages(0);
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err.message ||
          "Unable to fetch deal performance."
      );
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [CompID, buildFilterParams, viewModeValue, pageNumber, pageSizeValue]);

  const fetchSummary = useCallback(async () => {
    if (!CompID) return;

    if (summaryAbortRef.current) {
      summaryAbortRef.current.abort();
    }
    const controller = new AbortController();
    summaryAbortRef.current = controller;

    setSummaryLoading(true);
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_PERFORMANCE_SUMMARY, {
        params: buildFilterParams(),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const ok = response.data?.status === true || response.data?.Status === true;
      if (ok) {
        setSummary(response.data.data || response.data.Data || null);
      } else {
        setSummary(null);
        showErrorToast(
          response.data?.message || response.data?.Message || "Unable to fetch deal summary."
        );
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      console.error(err);
      setSummary(null);
      showErrorToast(
        err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err.message ||
          "Unable to fetch deal summary."
      );
    } finally {
      if (!controller.signal.aborted) setSummaryLoading(false);
    }
  }, [CompID, buildFilterParams]);

  useEffect(() => {
    if (isMain) fetchLocations();
  }, [isMain, fetchLocations]);

  useEffect(() => {
    if (!CompID) return;
    if (lastUserIdentityRef.current === userIdentity) return;
    lastUserIdentityRef.current = userIdentity;
    dispatch(setFilterField({ pageKey: PAGE_KEY, field: "location", value: DEFAULT_LOCATION }));
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, isMain, dispatch, fetchLocations]);

  useEffect(() => {
    fetchReport();
    return () => {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
    };
  }, [fetchReport]);

  const s = summary || {};
  const money = (v) =>
    `${getCurrencySymbol(compCurrency)} ${Number(v ?? 0).toFixed(2)}`;
  const activeView = viewModeValue;

  const summaryItems = [
    { label: "Net Sales", value: money(s.totalNetSales ?? s.TotalNetSales) },
    { label: "Qty Sold", value: Number(s.totalQuantitySold ?? s.TotalQuantitySold ?? 0) },
    { label: "Orders", value: Number(s.totalOrders ?? s.TotalOrders ?? 0) },
    { label: "Packages", value: Number(s.packageCount ?? s.PackageCount ?? 0) },
    {
      label: "Deal Net",
      value: money(s.dealNetSales ?? s.DealNetSales),
      hint: `Qty ${Number(s.dealQuantitySold ?? s.DealQuantitySold ?? 0)}`,
    },
    {
      label: "Combo Net",
      value: money(s.comboNetSales ?? s.ComboNetSales),
      hint: `Qty ${Number(s.comboQuantitySold ?? s.ComboQuantitySold ?? 0)}`,
    },
  ];

  return (
    <div>
      {(loading || summaryLoading) && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="#/report">Report</a>
            </li>
            <li className="breadcrumb-item active">Deal Performance</li>
          </ol>
          <h1 className="page-header mb-0">Deal Performance</h1>
        </div>
      </div>

      <div
        className="card mb-4"
        style={{
          position: "relative",
          zIndex: 10,
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          border: "none",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body p-3">
          <div className="row g-3 align-items-center mb-3">
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="filter-control">
                <div style={{ position: "relative", zIndex: 9999, width: "100%" }}>
                  <DateRangePicker
                    startDate={toValidDate(rawStartDate) || defaultDateRange[0]}
                    endDate={toValidDate(rawEndDate) || defaultDateRange[1]}
                    onChange={handleDateRangeChange}
                    placeholder="Select Date Range"
                    className="filter-dropdown"
                    allowSingleDate={true}
                    allowDateRange={true}
                    showTimeSelection={true}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-lg-4 col-md-6">
              <div className="search-input filter-control">
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search deal / combo name..."
                  value={search}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") setCommittedSearch(search.trim());
                  }}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "#333",
                  }}
                />
              </div>
            </div>

            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="filter-control">
                <Select
                  value={packageType}
                  onChange={(option) => handleFilterChange("packageType", option)}
                  options={PACKAGE_TYPE_OPTIONS}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder="Package Type"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="d-flex gap-2 justify-content-end flex-wrap">
                <button
                  className="clear-btn"
                  onClick={clearAllFilters}
                  title="Clear all filters"
                >
                  <i className="fa fa-times me-1"></i>
                  Filter
                </button>
                <button
                  className="btn btn-sm"
                  onClick={fetchSummary}
                  title="Load summary for current filters"
                  style={{
                    height: "32px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    borderRadius: "6px",
                  }}
                >
                  <i className="fa fa-chart-bar me-1"></i>
                  Summary
                </button>
                <button
                  className="btn btn-sm"
                  onClick={fetchReport}
                  title="Refresh"
                  style={{
                    height: "32px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    borderRadius: "6px",
                  }}
                >
                  <i className="fa fa-refresh me-1"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="row g-3 align-items-center">
            {isMain && locationOptions.length > 1 && (
              <div className="col-xl-2 col-lg-4 col-md-6">
                <div className="filter-control">
                  <Select
                    value={location}
                    onChange={(option) => handleFilterChange("location", option)}
                    options={locationOptions}
                    styles={dropdownStyles}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    placeholder="Location"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldBlockScroll={false}
                    menuShouldScrollIntoView={false}
                    closeMenuOnScroll={false}
                  />
                </div>
              </div>
            )}

            <div className="col-xl-2 col-lg-4 col-md-6">
              <div className="filter-control">
                <Select
                  value={viewMode}
                  onChange={(option) => handleFilterChange("viewMode", option)}
                  options={VIEW_MODE_OPTIONS}
                  styles={dropdownStyles}
                  isSearchable={false}
                  placeholder="View"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  closeMenuOnScroll={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {summary ? (
        <div className="row g-3 mb-4">
          {summaryItems.map((item) => (
            <div className="col-xl-2 col-lg-4 col-md-4 col-sm-6" key={item.label}>
              <div className="card h-100" style={{ borderTop: `3px solid ${themeColor}` }}>
                <div className="card-body py-3">
                  <div className="text-muted text-uppercase small fw-semibold mb-1">
                    {item.label}
                  </div>
                  <div className="fs-5 fw-bold">{item.value}</div>
                  {item.hint ? (
                    <div className="text-muted small mt-1">{item.hint}</div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="card" style={{ position: "relative", zIndex: 1 }}>
        <div className="card-body">
          <div className="table-responsive">
            {activeView === "packages" ? (
              <table className="table table-striped table-hover">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                  }}
                >
                  <tr>
                    <th>PACKAGE NAME</th>
                    <th>TYPE</th>
                    <th>QTY SOLD</th>
                    <th>GROSS SALES</th>
                    <th>DISCOUNTS</th>
                    <th>NET SALES</th>
                    <th>ORDERS</th>
                    <th>REFUND QTY</th>
                    <th>REFUND VALUE</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No deal or combo package sales in this range.
                      </td>
                    </tr>
                  ) : (
                    packages.map((row) => (
                      <tr
                        key={`${row.packageType || row.PackageType}-${row.packageID || row.PackageID}`}
                      >
                        <td>{row.packageName || row.PackageName}</td>
                        <td>{packageTypeLabel(row.packageType || row.PackageType)}</td>
                        <td>{Number(row.quantitySold ?? row.QuantitySold ?? 0)}</td>
                        <td style={{ color: "#0d6efd" }}>
                          {money(row.grossSales ?? row.GrossSales)}
                        </td>
                        <td>{money(row.discountAmount ?? row.DiscountAmount)}</td>
                        <td>{money(row.netSales ?? row.NetSales)}</td>
                        <td>{Number(row.orderCount ?? row.OrderCount ?? 0)}</td>
                        <td>{Number(row.refundQty ?? row.RefundQty ?? 0)}</td>
                        <td>{money(row.refundValue ?? row.RefundValue)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="table table-striped table-hover">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                  }}
                >
                  <tr>
                    <th>DATE</th>
                    <th>ORDER</th>
                    <th>PACKAGE</th>
                    <th>TYPE</th>
                    <th>QTY</th>
                    <th>TOTAL</th>
                    <th>STATUS</th>
                    <th>LOCATION</th>
                    <th>STAFF</th>
                  </tr>
                </thead>
                <tbody>
                  {details.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No package order lines in this range.
                      </td>
                    </tr>
                  ) : (
                    details.map((row, idx) => (
                      <tr key={`${row.orderID || row.OrderID}-${idx}`}>
                        <td>
                          {moment(row.orderDateTime || row.OrderDateTime).format(
                            "DD MMM YYYY HH:mm"
                          )}
                        </td>
                        <td>{row.orderID || row.OrderID}</td>
                        <td>{row.packageName || row.PackageName}</td>
                        <td>{packageTypeLabel(row.packageType || row.PackageType)}</td>
                        <td>{Number(row.quantity ?? row.Quantity ?? 0)}</td>
                        <td style={{ color: "#0d6efd" }}>
                          {money(row.totalAmount ?? row.TotalAmount)}
                        </td>
                        <td>{row.orderStatus || row.OrderStatus}</td>
                        <td>{row.locationName || row.LocationName || "—"}</td>
                        <td>{row.staffName || row.StaffName || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          <Pagination
            totalRecords={totalRecords}
            pageSize={pageSize}
            pageNumber={pageNumber}
            totalPages={totalPages}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            themeColor={themeColor}
          />
        </div>
      </div>
    </div>
  );
};

export default DealPerformanceReport;
